import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { ArrowRight, ArrowUp, MessageCircle, X } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { usePersonalization, PROFILES } from "@/lib/personalization";
import { useBooking } from "@/contexts/BookingContext";
import { trackEvent } from "@/lib/analytics";
import { useGoToGuide } from "@/components/GuideCta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const split = (s) => (s || "").split("|").map((x) => x.trim()).filter(Boolean);

const AssistantCtx = createContext(null);
export const useAssistant = () => useContext(AssistantCtx);

const sessionId = () => {
  let id = localStorage.getItem("de_chat_session");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("de_chat_session", id); }
  return id;
};

// Global floating "Spain Move Assistant". Route/page/article-aware; Radix Dialog gives focus trap, Escape and focus return.
export function SpainMoveAssistant({ children }) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState("launcher");
  const { pathname } = useLocation();
  const { profile } = usePersonalization();
  const hidden = pathname.startsWith("/admin");

  const openFrom = useCallback((p = "launcher") => { setPlacement(p); setOpen(true); }, []);
  useEffect(() => {
    if (open) trackEvent("chat_open", { route: profile || "neutral", page: pathname, placement });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AssistantCtx.Provider value={{ open: openFrom }}>
      {children}
      {!hidden && <AssistantUI open={open} setOpen={setOpen} />}
    </AssistantCtx.Provider>
  );
}

function AssistantUI({ open, setOpen }) {
  const { section } = useSiteContent();
  const c = section("assistant", HOME.assistant);
  const { profile, stage, setProfile } = usePersonalization();
  const { pathname } = useLocation();
  const { open: openBooking } = useBooking();
  const goGuide = useGoToGuide();
  const route = profile || "neutral";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const isArticle = pathname.startsWith("/blog/");
  const articleTitle = useMemo(() => (isArticle ? document.querySelector("h1")?.textContent?.trim() : null), [pathname, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const contextLine = isArticle && articleTitle ? (c.context_article || "").replace("{title}", articleTitle) : profile ? c[`context_${profile}`] : null;
  const quick = split(profile ? c[`quick_${profile}`] : c.quick_neutral);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [messages, busy]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    trackEvent("chat_message", { route, page: pathname });
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/chat`, { session_id: sessionId(), message: msg, route: profile || null, stage: stage || null, page: pathname, article_title: articleTitle || null });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, handoff: data.handoff }]);
      if (data.handoff) { setHandoff(true); trackEvent("chat_handoff", { route, page: pathname }); }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: c.error_text, error: true }]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const onQuick = (q) => {
    if (!profile) {
      const idx = ["Work from Spain", "Retire in Spain", "Buy a Home"].indexOf(q);
      if (idx > -1) {
        setProfile(PROFILES[idx]);
        trackEvent("chat_route_selected", { route: PROFILES[idx] });
        return;
      }
    }
    if (/something else|ask a question/i.test(q)) { inputRef.current?.focus(); return; }
    send(q);
  };

  const onCall = () => {
    trackEvent("chat_call_cta", { route, page: pathname, placement: "assistant" });
    setOpen(false);
    setTimeout(() => openBooking("assistant"), 150);
  };
  const onGuide = () => {
    trackEvent("chat_guide_cta", { route, page: pathname, placement: "assistant" });
    setOpen(false);
    setTimeout(() => goGuide("assistant"), 200);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          data-testid="assistant-launcher"
          aria-label={c.launcher_title}
          className="fixed z-[45] right-4 bottom-[calc(1.5rem_+_var(--de-consent-offset,0px))] sm:right-6 lg:bottom-6 flex items-center gap-2.5 bg-[#451C25] text-[#F5EFE7] pl-4 pr-5 py-3 shadow-lg shadow-[#451C25]/25 hover:bg-[#2B1017] transition-[background-color,bottom] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D] focus-visible:ring-offset-2"
          style={{ ...K, marginBottom: "env(safe-area-inset-bottom)" }}
        >
          <MessageCircle size={18} aria-hidden="true" />
          <span className="hidden md:inline text-[13px]">{c.launcher_title} <span className="font-medium text-[#B3A17D]">{c.launcher_short}</span></span>
          <span className="md:hidden text-[13px] font-medium">{c.launcher_short}</span>
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          data-testid="assistant-panel"
          aria-describedby={undefined}
          className="fixed z-[80] inset-0 sm:inset-auto sm:right-6 sm:bottom-6 sm:w-[420px] sm:max-h-[min(720px,calc(100vh-3rem))] flex flex-col bg-[#F5EFE7] text-[#451C25] shadow-2xl shadow-[#451C25]/30 border border-[#451C25]/10 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-4 motion-reduce:animate-none"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 border-b border-[#451C25]/10">
            <div>
              <p className="text-[#897756] text-[10px] tracking-[0.3em] uppercase font-semibold" style={K}>{c.panel_title}</p>
              <DialogPrimitive.Title className="mt-1.5 text-[22px] leading-tight" style={{ ...K, fontWeight: 600 }}>{c.opening}</DialogPrimitive.Title>
              <p className="mt-1.5 text-[#6B4954] text-[13px] leading-relaxed" style={S}>{contextLine || c.subcopy}</p>
            </div>
            <DialogPrimitive.Close data-testid="assistant-close" aria-label="Close" className="shrink-0 p-1.5 text-[#451C25]/70 hover:text-[#451C25] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]"><X size={18} /></DialogPrimitive.Close>
          </div>

          <div ref={listRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-[180px]">
            {messages.length === 0 && (
              <ul data-testid="assistant-quick" className="flex flex-wrap gap-2">
                {quick.map((q) => (
                  <li key={q}>
                    <button type="button" onClick={() => onQuick(q)} className="text-[12px] px-3 py-1.5 border border-[#451C25]/25 text-[#451C25] hover:border-[#451C25] hover:bg-white/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]" style={K}>{q}</button>
                  </li>
                ))}
              </ul>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : ""}>
                <p className={`max-w-[88%] text-[14px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#451C25] text-[#F5EFE7] px-4 py-2.5" : "text-[#451C25] border-l-2 border-[#B3A17D] pl-4"}`} style={S}>{m.content}</p>
              </div>
            ))}
            {busy && <p className="text-[#897756] text-[12px] tracking-[0.2em] uppercase" style={K} aria-live="polite">Thinking…</p>}
            {handoff && (
              <div data-testid="assistant-handoff" className="pt-2">
                <p className="text-[#6B4954] text-[13px] italic" style={S}>{c.handoff_text}</p>
              </div>
            )}
          </div>

          <form className="px-5 pt-2 pb-3 border-t border-[#451C25]/10" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <div className="flex items-end gap-2">
              <label htmlFor="assistant-input" className="sr-only">Your question</label>
              <textarea id="assistant-input" ref={inputRef} data-testid="assistant-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder={c.input_placeholder} className="flex-1 resize-none bg-white/60 border border-[#451C25]/20 px-3 py-2.5 text-[14px] text-[#451C25] placeholder:text-[#6B4954]/60 focus:outline-none focus:border-[#451C25]" style={S} />
              <button type="submit" data-testid="assistant-send" aria-label={c.send_label} disabled={busy || !input.trim()} className="p-2.5 bg-[#451C25] text-[#F5EFE7] disabled:opacity-40 hover:bg-[#2B1017] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]"><ArrowUp size={16} /></button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <button type="button" data-testid="assistant-call-cta" onClick={onCall} className={`group inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold px-4 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D] ${handoff ? "bg-[#451C25] text-[#F5EFE7] hover:bg-[#2B1017]" : "border border-[#451C25]/30 text-[#451C25] hover:border-[#451C25]"}`} style={K}>
                {c.call_label} <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <button type="button" data-testid="assistant-guide-cta" onClick={onGuide} className="text-[12px] text-[#451C25] underline underline-offset-4 decoration-[#B3A17D] hover:decoration-[#451C25] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]" style={{ ...K, fontWeight: 500 }}>{c.guide_label}</button>
            </div>
            <p className="mt-2.5 text-[#6B4954]/80 text-[11px] leading-snug" style={S}>{c.call_meta} · {c.disclaimer}</p>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

